import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  PaulAdminApiService,
  PaulCoachAskResponse,
  PaulConfidence,
  PaulConversationRun,
  PaulFeedbackRequest,
  PaulVerificationStatus,
} from '../../services/paul-admin/paul-admin-api.service';

interface ChatTurn {
  id: string;
  role: 'user' | 'paul';
  text: string;
  createdAt: Date;
  runUuid?: string;
  model?: string | null;
  latencyMs?: number | null;
  toolCount?: number;
  run?: PaulCoachAskResponse;
}

@Component({
  selector: 'app-paul-chat',
  templateUrl: './paul-chat.component.html',
  styleUrls: ['./paul-chat.component.scss'],
})
export class PaulChatComponent implements OnInit {
  private readonly conversationStorageKey = 'paul_admin_conversation_uuid';

  prompt = '';
  asking = false;
  restoringConversation = false;
  coaching = false;
  errorMessage = '';
  successMessage = '';
  isDark = localStorage.getItem('paul_admin_theme') !== 'light';
  turns: ChatTurn[] = [];
  selectedRun: PaulCoachAskResponse | null = null;
  conversationUuid: string | null = null;
  conversationTurnCount = 0;

  lessonTypes = [
    'intent',
    'contact_resolution',
    'product_resolution',
    'location_resolution',
    'zone_resolution',
    'freight_metrics',
    'rating_method',
    'rate_selection',
    'fallback_order',
    'answer_style',
    'data_gap',
    'other',
  ];
  lessonType = 'other';
  severity: 'low' | 'medium' | 'high' = 'medium';
  coachConfidence: PaulConfidence = 'medium';
  verificationStatus: PaulVerificationStatus = 'pending';
  title = '';
  correction = '';
  expectedBehavior = '';
  expectedAnswer = '';
  sourceOfTruth = '';
  evidenceNote = '';
  businessNotes = '';
  technicalDiagnosis = '';

  constructor(
    private readonly paulApi: PaulAdminApiService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    const storedUuid = sessionStorage.getItem(this.conversationStorageKey);
    if (!storedUuid || !this.isUuid(storedUuid)) {
      sessionStorage.removeItem(this.conversationStorageKey);
      return;
    }

    this.restoreConversation(storedUuid);
  }

  ask(): void {
    const message = this.prompt.trim();
    if (!message || this.asking) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';
    this.prompt = '';
    this.asking = true;
    this.turns.push({ id: crypto.randomUUID(), role: 'user', text: message, createdAt: new Date() });

    this.paulApi.ask(message, this.conversationUuid || undefined).subscribe({
      next: response => {
        this.asking = false;
        if (!response.ok) {
          this.errorMessage = response.message || 'PAUL could not answer that request.';
          return;
        }

        this.selectedRun = response;
        this.conversationUuid = response.conversation_uuid;
        this.conversationTurnCount = response.conversation_turn_count;
        sessionStorage.setItem(this.conversationStorageKey, response.conversation_uuid);
        this.turns.push({
          id: response.coach_run_uuid,
          role: 'paul',
          text: response.answer || 'PAUL returned no user-facing answer.',
          createdAt: new Date(),
          runUuid: response.coach_run_uuid,
          model: response.chat_result['model'] || null,
          latencyMs: response.latency_ms,
          toolCount: response.trace_summary.tool_count || 0,
          run: response,
        });
        this.resetCoachForm();
      },
      error: error => {
        this.asking = false;
        this.errorMessage = this.errorText(error, 'PAUL is unavailable right now.');
      },
    });
  }

  handlePromptKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.ask();
    }
  }

  selectRun(turn: ChatTurn): void {
    if (turn.run) {
      this.selectedRun = turn.run;
      this.resetCoachForm();
      return;
    }
    if (!turn.runUuid) {
      return;
    }

    this.errorMessage = '';
    this.paulApi.getRun(turn.runUuid).subscribe({
      next: response => {
        const run = response.run;
        this.selectedRun = {
          ok: true,
          coach_run_uuid: run.coach_run_uuid,
          conversation_uuid: run.conversation_uuid || this.conversationUuid || '',
          conversation_turn_count: this.conversationTurnCount,
          context_turns: 0,
          prompt: run.prompt,
          answer: run.answer,
          chat_result: run.response_payload || {},
          trace_summary: run.trace_summary,
          latency_ms: run.latency_ms || 0,
        };
        turn.run = this.selectedRun;
        this.resetCoachForm();
      },
      error: error => {
        this.errorMessage = this.errorText(error, 'The selected PAUL run could not be loaded.');
      },
    });
  }

  submitCoaching(): void {
    if (!this.selectedRun || this.coaching) {
      return;
    }
    if (!this.correction.trim() && !this.expectedBehavior.trim() && !this.expectedAnswer.trim()) {
      this.errorMessage = 'Add a correction, expected behavior, or expected answer before saving the lesson.';
      return;
    }

    const request: PaulFeedbackRequest = {
      coach_run_uuid: this.selectedRun.coach_run_uuid,
      lesson_type: this.lessonType,
      severity: this.severity,
      title: this.title.trim() || undefined,
      correction: this.correction.trim() || undefined,
      expected_behavior: this.expectedBehavior.trim() || undefined,
      expected_answer: this.expectedAnswer.trim() || undefined,
      coach_confidence: this.coachConfidence,
      source_of_truth: this.sourceOfTruth.trim() || undefined,
      evidence_payload: this.evidenceNote.trim() ? { note: this.evidenceNote.trim() } : undefined,
      business_notes: this.businessNotes.trim() || undefined,
      technical_diagnosis: this.technicalDiagnosis.trim() || undefined,
      verification_status: this.verificationStatus,
    };

    this.errorMessage = '';
    this.successMessage = '';
    this.coaching = true;
    this.paulApi.submitFeedback(request).subscribe({
      next: response => {
        this.coaching = false;
        this.successMessage = `Coaching lesson ${response.lesson_uuid.slice(0, 8)} saved for review.`;
      },
      error: error => {
        this.coaching = false;
        this.errorMessage = this.errorText(error, 'The coaching lesson could not be saved.');
      },
    });
  }

  newConversation(): void {
    sessionStorage.removeItem(this.conversationStorageKey);
    this.conversationUuid = null;
    this.conversationTurnCount = 0;
    this.turns = [];
    this.selectedRun = null;
    this.prompt = '';
    this.errorMessage = '';
    this.successMessage = '';
    this.resetCoachForm();
  }

  openCoachingData(): void {
    void this.router.navigate(['/paul/coaching']);
  }

  toggleTheme(): void {
    this.isDark = !this.isDark;
    localStorage.setItem('paul_admin_theme', this.isDark ? 'dark' : 'light');
  }

  pretty(value: unknown): string {
    return JSON.stringify(value ?? {}, null, 2);
  }

  private resetCoachForm(): void {
    this.lessonType = 'other';
    this.severity = 'medium';
    this.coachConfidence = 'medium';
    this.verificationStatus = 'pending';
    this.title = '';
    this.correction = '';
    this.expectedBehavior = '';
    this.expectedAnswer = '';
    this.sourceOfTruth = '';
    this.evidenceNote = '';
    this.businessNotes = '';
    this.technicalDiagnosis = '';
  }

  private restoreConversation(conversationUuid: string): void {
    this.restoringConversation = true;
    this.paulApi.getConversation(conversationUuid).subscribe({
      next: response => {
        this.restoringConversation = false;
        if (!response.ok) {
          sessionStorage.removeItem(this.conversationStorageKey);
          return;
        }

        this.conversationUuid = response.conversation.conversation_uuid;
        this.conversationTurnCount = response.conversation.turn_count;
        this.turns = [];
        for (const run of response.runs) {
          this.restoreRun(run);
        }
      },
      error: error => {
        this.restoringConversation = false;
        if (error instanceof HttpErrorResponse && error.status === 404) {
          sessionStorage.removeItem(this.conversationStorageKey);
          return;
        }
        this.errorMessage = this.errorText(error, 'The previous PAUL conversation could not be restored.');
      },
    });
  }

  private restoreRun(run: PaulConversationRun): void {
    const createdAt = new Date(run.created_at);
    this.turns.push({
      id: `user-${run.coach_run_uuid}`,
      role: 'user',
      text: run.prompt,
      createdAt,
    });
    this.turns.push({
      id: run.coach_run_uuid,
      role: 'paul',
      text: run.answer || 'PAUL returned no user-facing answer.',
      createdAt,
      runUuid: run.coach_run_uuid,
      model: run.model,
      latencyMs: run.latency_ms,
      toolCount: run.trace_summary.tool_count || 0,
    });
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private errorText(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || error.error?.error || fallback;
    }
    return fallback;
  }
}
